class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.activeElement = null;
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.bindOverlayClose();
    this.bindViewCartLink();
    this.hideAssistiveNoise();
    this.noiseObserver = new MutationObserver(() => {
      this.hideAssistiveNoise();
      this.keepFocusInside();
    });
    this.noiseObserver.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-live', 'role'],
    });
    this.setHeaderCartIconAccessibility();
    this.allowOtherModals();
  }

  // Popups such as Klaviyo's signup form can open on top of the drawer. Dawn's focus
  // trap (a document focusin listener) would pull focus straight back, leaving the
  // popup open but unreachable by keyboard. Let focus stay inside another modal dialog.
  allowOtherModals() {
    if (window.distaDrawerModalGuard) return;
    window.distaDrawerModalGuard = true;
    document.addEventListener(
      'focusin',
      (event) => {
        const drawer = document.querySelector('cart-drawer.active');
        const target = event.target;
        if (!drawer || !(target instanceof Element) || drawer.contains(target)) return;
        const modal = target.closest('[aria-modal="true"], [role="dialog"], [role="alertdialog"]');
        if (modal && !drawer.contains(modal)) event.stopPropagation();
      },
      true
    );
  }

  hideAssistiveNoise() {
    this.querySelectorAll('style, script').forEach((node) => {
      node.setAttribute('aria-hidden', 'true');
    });
    this.silenceLiveRegions();
  }

  // STOPGAP: app widgets in the drawer (shipping bar, free gift) are live regions that
  // re-render on every DOM change, so their text was read again on each focus move.
  // Cart changes are announced by DistaA11y.announce; only error alerts stay live.
  silenceLiveRegions() {
    const keep = '#CartDrawer-CartErrors, .cart-item__error';
    this.querySelectorAll('[aria-live], [role="status"], [role="log"], [role="alert"], [role="marquee"], [role="timer"]').forEach(
      (node) => {
        if (node.matches(keep)) return;
        if (node.getAttribute('aria-live') !== 'off') node.setAttribute('aria-live', 'off');
        if (node.hasAttribute('role')) node.removeAttribute('role');
      }
    );
  }

  getCartTrigger() {
    return document.querySelector('#cart-icon-bubble');
  }

  bindOverlayClose() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (!overlay) return;
    overlay.addEventListener('click', this.close.bind(this));
  }

  bindViewCartLink() {
    this.addEventListener('keydown', (event) => {
      const link = event.target instanceof Element && event.target.closest('a.view-full-cart');
      if (!link || !this.contains(link) || event.code !== 'Space') return;
      event.preventDefault();
      const url = link.getAttribute('href');
      if (url) window.location.assign(url);
    });

    this.addEventListener('click', (event) => {
      const link = event.target instanceof Element && event.target.closest('a.view-full-cart');
      if (!link || !this.contains(link)) return;
      const url = link.getAttribute('href');
      if (!url) return;
      event.preventDefault();
      window.location.assign(url);
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    const path = window.location.pathname;
    const isCartPage = /\/cart(\/)?$/.test(path);

    if (!cartLink || isCartPage) return;

    cartLink.removeAttribute('aria-controls');
    cartLink.removeAttribute('aria-describedby');
    cartLink.removeAttribute('aria-haspopup');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  getDialogElement() {
    return this.querySelector('.drawer__inner') || document.getElementById('CartDrawer') || this;
  }

  getInitialFocusElement() {
    const dialog = this.getDialogElement();
    return (
      dialog.querySelector('.drawer__close') ||
      dialog.querySelector(
        'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ) ||
      dialog
    );
  }

  setDrawerHidden(hidden) {
    const dialog = this.querySelector('[role="dialog"]');
    if (hidden) {
      this.setAttribute('aria-hidden', 'true');
      if (dialog) dialog.setAttribute('aria-hidden', 'true');
      return;
    }
    this.removeAttribute('aria-hidden');
    if (dialog) dialog.removeAttribute('aria-hidden');
  }

  focusCartTrigger() {
    const trigger = this.getCartTrigger();
    if (!trigger || this.classList.contains('active')) return;
    Array.from(trigger.children).forEach((child) => child.setAttribute('aria-hidden', 'true'));
    trigger.removeAttribute('aria-controls');
    trigger.removeAttribute('aria-describedby');
    trigger.removeAttribute('aria-description');
    trigger.removeAttribute('aria-haspopup');
    trigger.focus();
  }

  focusIsInside() {
    const active = document.activeElement;
    return !!active && active !== document.body && this.getDialogElement().contains(active);
  }

  // Drawer contents are re-rendered after Add to Cart (cart update, free-shipping
  // recommendations, Wiser). If that drops focus to <body> or leaves it on the
  // trigger behind the open drawer, move it back in straight away.
  keepFocusInside() {
    if (!this.classList.contains('active') || this.focusIsInside()) return;
    const active = document.activeElement;
    const stranded = !active || active === document.body || active === this.activeElement;
    if (!stranded || window.distaCartFocus) return;
    this.trapDrawerFocus();
  }

  trapDrawerFocus() {
    if (!this.classList.contains('active')) return;
    const dialog = this.getDialogElement();
    const preferred = window.resolveDistaCartFocus?.();
    const initial = preferred && dialog.contains(preferred) ? preferred : this.getInitialFocusElement();
    trapFocus(dialog, initial);
  }

  open(triggeredBy) {
    if (triggeredBy && !this.contains(triggeredBy)) {
      this.setActiveElement(triggeredBy);
    } else if (!this.activeElement) {
      const candidate = document.activeElement;
      if (candidate && candidate !== document.body && !this.contains(candidate)) {
        this.setActiveElement(candidate);
      }
    }

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    const alreadyOpen = this.classList.contains('active');
    this.classList.add('animate', 'active');
    document.body.classList.add('overflow-hidden');
    this.setDrawerHidden(false);

    if (alreadyOpen) {
      this.trapDrawerFocus();
      return;
    }

    // Focus as soon as the drawer can take it; the later attempts only act if
    // focus still isn't inside, so they never pull a user back to the first control.
    const activateTrap = () => {
      if (!this.focusIsInside()) this.trapDrawerFocus();
    };
    activateTrap();
    requestAnimationFrame(() => requestAnimationFrame(activateTrap));
    this.addEventListener('transitionend', activateTrap, { once: true });
    setTimeout(activateTrap, 300);
  }

  close() {
    window.distaCartFocus = null;
    const active = document.activeElement;
    if (active instanceof HTMLElement && this.contains(active)) active.blur();
    this.classList.remove('active');
    this.setDrawerHidden(true);
    removeTrapFocus();
    document.body.classList.remove('overflow-hidden');
    requestAnimationFrame(() => this.focusCartTrigger());
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.bindOverlayClose();
      this.open(this.activeElement);
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
